import React, { createContext, useReducer, ReactNode, useEffect } from "react";
import { appStateReducer } from "./AppReducer";
import { historyList, historyEnsure, assistantsList, assistantsEnsure, ChatMessage } from "../api";
import { Conversation, Assistant, ChatHistoryLoadingState, CosmosDBHealth, CosmosDBStatus, AssistantsLoadingState } from "../api";
import { useCookies } from "react-cookie";
import { v4 as uuidv4 } from "uuid";

export interface AppState {
    isChatHistoryOpen: boolean;
    chatHistoryLoadingState: ChatHistoryLoadingState;
    isCosmosDBAvailable: CosmosDBHealth;
    chatHistory: Conversation[] | null;
    filteredChatHistory: Conversation[] | null;
    currentChat: Conversation | null;
    assistant: Assistant | null;
    assistants: Assistant[] | null;
    assistantsLoadingState: AssistantsLoadingState;
    previousChat: Conversation | null;
    answerLoading: boolean;
    loginExpired: boolean;
    loginExpiredLoading: boolean;
}

export type Action =
    | { type: "TOGGLE_CHAT_HISTORY" }
    | { type: "SET_COSMOSDB_STATUS"; payload: CosmosDBHealth }
    | { type: "UPDATE_CHAT_HISTORY_LOADING_STATE"; payload: ChatHistoryLoadingState }
    | { type: "UPDATE_CURRENT_CHAT"; payload: Conversation | null }
    | { type: "UPDATE_FILTERED_CHAT_HISTORY"; payload: Conversation[] | null }
    | { type: "UPDATE_CHAT_HISTORY"; payload: Conversation } // API Call
    | { type: "UPDATE_CHAT_TITLE"; payload: Conversation } // API Call
    | { type: "DELETE_CHAT_ENTRY"; payload: string } // API Call
    | { type: "DELETE_CHAT_HISTORY" } // API Call
    | { type: "DELETE_CURRENT_CHAT_MESSAGES"; payload: string } // API Call
    | { type: "FETCH_CHAT_HISTORY"; payload: Conversation[] | null } // API Call
    | { type: "UPDATE_ASSISTANT"; payload: Assistant | null }
    | { type: "FETCH_ASSISTANTS"; payload: Assistant[] | null } // API Call
    | { type: "UPDATE_ASSISTANTS_LOADING_STATE"; payload: AssistantsLoadingState }
    | { type: "TOGGLE_ANSWER_LOADING" }
    | { type: "UPDATE_CHAT_HISTORY_MESSAGE"; payload: { convId: string; message: ChatMessage } } // API Call
    | { type: "SET_LOGIN_EXPIRED" }
    | { type: "SET_LOGIN_EXPIRED_LOADING"; payload: boolean };

const initialState: AppState = {
    isChatHistoryOpen: false,
    chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
    chatHistory: null,
    filteredChatHistory: null,
    currentChat: null,
    isCosmosDBAvailable: {
        cosmosDB: false,
        status: CosmosDBStatus.NotConfigured
    },
    assistant: null,
    assistants: null,
    assistantsLoadingState: AssistantsLoadingState.Loading,
    previousChat: null,
    answerLoading: false,
    loginExpired: false,
    loginExpiredLoading: true
};

export const AppStateContext = createContext<
    | {
          state: AppState;
          dispatch: React.Dispatch<Action>;
      }
    | undefined
>(undefined);

type AppStateProviderProps = {
    children: ReactNode;
};

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(appStateReducer, initialState);
    const [cookies, setCookie] = useCookies(["session"]);

    useEffect(() => {
        const handleCookie = () => {
            if (!cookies.session) {
                const newSessionId = uuidv4();
                setCookie("session", newSessionId, { path: "/" });
            }
        };

        handleCookie();
        const fetchChatHistory = async (): Promise<Conversation[] | null> => {
            try {
                const response = await historyList(cookies.session);
                if (response) {
                    dispatch({ type: "FETCH_CHAT_HISTORY", payload: response });
                } else {
                    dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
                }
                return response;
            } catch (err) {
                dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
                console.error("There was an issue fetching your data.");
                return null;
            }
        };

        const getHistoryEnsure = async () => {
            dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Loading });
            try {
                const response = await historyEnsure();
                if (response?.cosmosDB) {
                    const res = await fetchChatHistory();
                    if (res) {
                        dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Success });
                        dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                    } else {
                        dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                        dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                    }
                } else {
                    dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                }
            } catch (err) {
                dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } });
            }
        };

        getHistoryEnsure();
    }, [cookies, setCookie]);

    useEffect(() => {
        const fetchAssistants = async (): Promise<Assistant[] | null> => {
            try {
                const response = await assistantsList();
                if (response) {
                    dispatch({ type: "FETCH_ASSISTANTS", payload: response });
                    dispatch({ type: "UPDATE_ASSISTANT", payload: response[0] });
                } else {
                    dispatch({ type: "FETCH_ASSISTANTS", payload: null });
                }
                return response;
            } catch (err) {
                dispatch({ type: "FETCH_ASSISTANTS", payload: null });
                console.error("There was an issue fetching your assistants.");
                return null;
            }
        };

        const getAssistantsEnsure = async () => {
            dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Loading });
            try {
                const response = await assistantsEnsure();
                if (response?.cosmosDB) {
                    const res = await fetchAssistants();
                    if (res) {
                        dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Success });
                        dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                    } else {
                        dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                        dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                    }
                } else {
                    dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                }
            } catch (err) {
                dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } });
            }
        };

        getAssistantsEnsure();
    }, []);

    return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>;
};
